import React from 'react';
import { Lock, ShoppingCart, SwitchUser } from '@gjensidige/builders-icons';
import { Claim, Housing } from '@gjensidige/builders-icons/products';
import { Nav } from '@gjensidige/core-navigation';

import { useBasketCount } from './hooks/useBasketCount';
import { useClaimsWithUnreadDocumentsCount } from './hooks/useUnreadDocumentsCount';
import { useUserEligibility } from './hooks/useUserEligibility';
import { ROUTE } from './utils/routes';
import {
  buildCommercialPensionMembersUrl,
  buildCommercialPensionUrl,
  clearCache,
  hideInsuranceCommercial,
  hideKonseroversikt,
  isOnlyPrivatKunde,
  postkasseUnread,
  selectedCommercialPensionAgreement,
  showByttBrukerLink
} from './utils/utils';
import { NavDropdownContent } from './components/NavDropdownContent';

import styles from './index.module.less';

export const configCommercial = (): Config => ({
  menuItems: [
    {
      label: 'Oversikt',
      className: styles.minOversikt,
      icon: <Housing width="24px" height="24px" />,
      href: '',
      desktopOnly: true
    },
    {
      label: 'Forsikring',
      subMenuItems: [
        { label: 'Forsikringer', href: '' },
        { label: 'Betaling og faktura', href: '' },
        { label: 'Skadesaker', href: '' },
        { label: 'Forsikringsbekreftelser', href: '' },
        { label: 'Bedriftsopplysninger', href: '' },
        {
          label: 'Konsernoversikt',
          href: '',
          hideCondition: hideKonseroversikt()
        }
      ],
      hideCondition: hideInsuranceCommercial(),
      subMenuCTAs: [
        {
          label: 'Meld skade',
          href: '',
          desktopOnly: true
        },
        {
          label: 'Kundeutbytte',
          href: ''
        },
        { label: 'Kjøp forsikring', href: '/naringsliv/forsikring' }
      ]
    },
    {
      label: 'Pensjon',
      ...(selectedCommercialPensionAgreement()
        ? {
            subMenuItems: [
              { label: 'Avtaleopplysninger', href: buildCommercialPensionUrl('details', true) },
              { label: 'Betaling og faktura', href: buildCommercialPensionUrl('betalingsoversikt') },
              { label: 'Bedriftens ansatte', href: buildCommercialPensionMembersUrl() },
              { label: 'Årsoppgave', href: buildCommercialPensionUrl('aarsoppgave') },
              { label: 'Ansattes pensjonskapital', href: buildCommercialPensionUrl('capital') },
              { label: 'Pensjonsgivende lønn', href: buildCommercialPensionUrl('wage-categories') },
              { label: 'Innskuddsfond', href: buildCommercialPensionUrl('fund') },
              { label: 'Rapporter', href: buildCommercialPensionUrl('report') }
            ]
          }
        : { href: '' })
    },
    {
      label: 'Postkasse',
      badge: postkasseUnread(),
      href: ''
    },
    {
      label: 'Innstillinger',
      href: ''
    },
    {
      label: 'Bytt bruker',
      ...(showByttBrukerLink()
        ? { href: '' }
        : {
            subMenuItems: NavDropdownContent().map((bedrift) => ({
              label: bedrift?.label,
              icon: bedrift?.icon,
              href: bedrift?.href
            }))
          }),
      icon: <SwitchUser />,
      mobileOnly: true
    },
    {
      label: 'Logg ut',
      href: '',
      icon: <Lock />,
      mobileOnly: true
    }
  ]
});

export const useConfigPrivate = (): Config => {
  const { data: unreadDocumentsCount } = useClaimsWithUnreadDocumentsCount();
  const { data: basketCount } = useBasketCount();
  const { data: eligibility } = useUserEligibility();

  return {
    menuItems: [
      {
        label: 'Anmeld skade',
        icon: <Claim />,
        href: ROUTE.ANMELD_SKADE,
        mobileOnly: true
      },
      { label: 'Kurv', icon: <ShoppingCart />, href: ROUTE.KURV, badge: basketCount, mobileOnly: true },
      {
        label: 'Oversigt',
        icon: <Housing width="24px" height="24px" />,
        href: ROUTE.MINE_FORSIKRINGER
      },
      {
        label: 'Forsikringer',
        href: ROUTE.FORSIKRINGER
      },
      {
        label: 'Skade',
        subMenuItems: [
          { label: 'Anmeld skade', href: ROUTE.ANMELD_SKADE },
          { label: 'Mine skader', href: ROUTE.MINE_SKADER }
        ]
      },
      {
        label: 'Indbakke',
        href: ROUTE.MINE_DOKUMENTER,
        badge: unreadDocumentsCount,
        hideCondition: !(eligibility?.iditEligible || eligibility?.f2100Eligible)
      },
      {
        label: 'Profil',
        subMenuItems: [
          { label: 'Mine kontaktoplysninger', href: ROUTE.MINE_KONTAKTOPLYSNINGER },
          { label: 'Mine samtykker', href: ROUTE.MINE_SAMTYKKER }
        ]
      },
      {
        label: 'Mine data',
        subMenuItems: [
          { label: 'Udlevering af data', href: ROUTE.PORTABILITET },
          { label: 'Indsigt i min kundedata', href: ROUTE.INDSIGT }
        ]
      },
      {
        label: 'Skift bruger',
        ...(showByttBrukerLink()
          ? { href: '' }
          : {
              subMenuItems: NavDropdownContent().map((bedrift) => ({
                label: bedrift?.label,
                icon: bedrift?.icon,
                href: bedrift?.href
              }))
            }),
        icon: <SwitchUser />,
        mobileOnly: true,
        hideCondition: isOnlyPrivatKunde()
      },
      {
        label: 'Log ud',
        href: ROUTE.LOGOUT,
        icon: <Lock />,
        className: styles.navItemCenter,
        mobileOnly: true,
        onClick: clearCache
      }
    ]
  };
};

export type SubMenuCTA = Parameters<typeof Nav.CTA>[0] & {
  hideCondition?: boolean;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
};

export type SubMenuItem = Parameters<typeof Nav.Item>[0] & {
  hideCondition?: boolean;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
};

export type MenuItem = Parameters<typeof Nav.Item>[0] & {
  subMenuItems?: SubMenuItem[];
  subMenuCTAs?: SubMenuCTA[];
  hideCondition?: boolean;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
};

export type Config = {
  menuItems: MenuItem[];
};
